import { useEffect, useState, useMemo, useRef } from 'react';
import { useFigureCompositionStore } from '@/store/figure-composition';
import { usePlotsStore } from '@/store/plots';
import { Loader2 } from 'lucide-react';

export function FigureCaption() {
  const { 
    currentComposition, 
    setCaptionManual,
    recomputeAutoCaption
  } = useFigureCompositionStore();
  const { loadInsightsForPlots, plots, plotCaptions } = usePlotsStore();
  const [isLoadingCaptions, setIsLoadingCaptions] = useState(false);
  const [hasUserEdited, setHasUserEdited] = useState(false);
  const lastPanelSignatureRef = useRef<string>('');

  if (!currentComposition) return null;

  const captionAuto = currentComposition.captionAuto || '';
  const captionManual = currentComposition.captionManual || currentComposition.caption || '';
  
  // Check if any panel plots are missing captions
  const panelsWithPlots = currentComposition.panels.filter(p => p.plotId);
  
  // Create stable signature from sorted unique {plotId, versionId} pairs
  // CRITICAL: Use effectiveVersionId (resolved from store) instead of null for follow-latest panels
  const panelSignature = useMemo(() => {
    const { resolveEffectivePlotVersionSync } = useFigureCompositionStore.getState();
    
    const uniquePairs = Array.from(
      new Map(
        panelsWithPlots.map(p => {
          // Resolve effective version ID synchronously from store
          const resolved = resolveEffectivePlotVersionSync({
            plotId: p.plotId,
            panelVersionId: p.plotVersionId,
            projectId: currentComposition?.projectId,
          });
          // Use effectiveVersionId, or 'latest' as stable string if still null
          const versionIdForSignature = resolved.versionId ?? 'latest';
          const key = `${p.plotId}:${versionIdForSignature}`;
          return [key, { plotId: p.plotId, versionId: versionIdForSignature }];
        })
      ).values()
    );
    
    const sorted = uniquePairs.sort((a, b) => 
      a.plotId.localeCompare(b.plotId) || 
      (a.versionId ?? '').localeCompare(b.versionId ?? '')
    );
    
    return JSON.stringify(sorted);
  }, [panelsWithPlots, currentComposition?.projectId]);
  
  const plotIds = panelsWithPlots.map(p => p.plotId).filter((id): id is string => !!id);
  
  // Use manual caption if user has edited, otherwise show auto as ghost text
  const displayValue = hasUserEdited || captionManual ? captionManual : (captionAuto || '');
  const isShowingGhostText = !hasUserEdited && !captionManual && !!captionAuto;

  // Load insights and recompute caption when panel signature changes
  useEffect(() => {
    if (plotIds.length > 0 && panelSignature !== lastPanelSignatureRef.current) {
      lastPanelSignatureRef.current = panelSignature;
      setIsLoadingCaptions(true);
      // Always try to load insights - the function will skip if already cached with non-null values
      loadInsightsForPlots(currentComposition.projectId, plotIds)
        .then(() => {
          setIsLoadingCaptions(false);
          // Recompute caption after insights load - use a small delay to ensure store state is updated
          setTimeout(async () => {
            console.log('[FigureCaption] Insights loaded, recomputing caption for panelSignature:', panelSignature);
            await recomputeAutoCaption();
          }, 200);
        })
        .catch((err) => {
          console.warn('[FigureCaption] Failed to load insights:', err);
          setIsLoadingCaptions(false);
          // Still try to recompute with whatever we have
          setTimeout(async () => {
            await recomputeAutoCaption();
          }, 200);
        });
    } else if (plotIds.length === 0) {
      setIsLoadingCaptions(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentComposition.projectId, panelSignature]);

  // Also recompute when plot captions become available in the store
  useEffect(() => {
    if (plotIds.length > 0 && !hasUserEdited && panelSignature !== lastPanelSignatureRef.current) {
      const { getPlotCaption } = usePlotsStore.getState();
      const plotsWithCaptions = plotIds.map(plotId => {
        const caption = getPlotCaption(plotId, currentComposition.projectId);
        return { plotId, hasCaption: !!caption, caption };
      });
      
      const hasAllCaptions = plotsWithCaptions.every(p => p.hasCaption);
      const hasAnyCaptions = plotsWithCaptions.some(p => p.hasCaption);
      
      // If we have captions but no auto caption yet, recompute
      if (hasAnyCaptions && !captionAuto) {
        console.log('[FigureCaption] Captions available but auto caption missing, recomputing...');
        recomputeAutoCaption().catch(err => console.warn('[FigureCaption] Failed to recompute caption:', err));
      }
      // If all captions are loaded and auto caption is empty or outdated, recompute
      else if (hasAllCaptions) {
        // Check if we need to recompute (e.g., if caption count doesn't match panel count)
        const expectedLines = plotIds.length;
        const currentLines = captionAuto.split('\n').filter(l => l.trim()).length;
        if (currentLines !== expectedLines) {
          console.log('[FigureCaption] Caption line count mismatch, recomputing...', { expectedLines, currentLines });
          recomputeAutoCaption().catch(err => console.warn('[FigureCaption] Failed to recompute caption:', err));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plots, plotCaptions, panelSignature, captionAuto, currentComposition.projectId]);

  // Track if user has manually edited
  // User has edited if captionManual exists and differs from the original caption
  useEffect(() => {
    const originalCaption = currentComposition.caption || '';
    // User has edited if they've typed something, or if captionManual exists and differs from original
    const edited = captionManual !== '' && captionManual !== originalCaption;
    setHasUserEdited(edited);
  }, [captionManual, currentComposition.caption]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setHasUserEdited(true);
    setCaptionManual(value);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-foreground">Caption</label>
        {isLoadingCaptions && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            <span>Loading...</span>
          </div>
        )}
      </div>
      
      <textarea
        className={`border rounded px-2 py-1 text-sm resize-y min-h-[80px] w-full ${
          isShowingGhostText ? 'text-muted-foreground italic' : ''
        }`}
        value={displayValue}
        onChange={handleChange}
        onFocus={() => {
          // When user focuses and we're showing ghost text, copy it to manual so they can edit it
          if (isShowingGhostText && captionAuto) {
            setHasUserEdited(true);
            setCaptionManual(captionAuto);
          }
        }}
        placeholder={!captionAuto ? 'Caption will be generated from panel plots' : ''}
      />
    </div>
  );
}


